import { CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useCompleteOnboarding } from "@/features/workers/model/useWorkerProfile";
import {
  EXPERIENCE_LEVEL_LABELS,
  LEGAL_STATUS_LABELS,
  type WorkerProfile,
} from "@/features/workers/types";

interface Props {
  profile: WorkerProfile;
  email: string | undefined;
  phone: string | null;
  onBack: () => void;
}

export function ReviewStep({ profile, email, phone, onBack }: Props) {
  const navigate = useNavigate();
  const complete = useCompleteOnboarding();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await complete.mutateAsync();
    navigate("/worker", { replace: true });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Looks good?
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          We'll submit your profile for review. Most approvals happen within 24 hours.
        </p>
      </div>

      <div className="rounded-md border border-border bg-surface">
        <SummaryRow label="Name" value={profile.name} />
        <SummaryRow label="Email" value={email ?? "Not set"} />
        <SummaryRow label="Phone" value={phone ?? "Not set"} />
        <SummaryRow label="City" value={profile.city ?? "Not set"} />
        <SummaryRow
          label="Eligibility"
          value={
            profile.legal_status ? LEGAL_STATUS_LABELS[profile.legal_status] : "Not set"
          }
        />
        <SummaryRow
          label="ID document"
          value={
            profile.eligibility_document_url ? "Attached ✓" : "Missing"
          }
        />
        <SummaryRow
          label="Canadian SIN"
          value={profile.sin_provided ? "Provided ✓" : "Not provided"}
        />
        {profile.legal_status === "OPEN_WORK_PERMIT" && (
          <SummaryRow
            label="Work permit expires"
            value={profile.work_permit_expires_on ?? "Not set"}
          />
        )}
        <SummaryRow
          label="Experience"
          value={
            profile.experience_level
              ? EXPERIENCE_LEVEL_LABELS[profile.experience_level]
              : "Not set"
          }
        />
        <SummaryRow
          label="Min hourly rate"
          value={
            profile.min_hourly_rate && Number(profile.min_hourly_rate) > 0
              ? `$${Number(profile.min_hourly_rate).toFixed(2)}/hr`
              : "No floor"
          }
        />
        <SummaryRow
          label="Photo"
          value={profile.photo_url ? "Uploaded ✓" : "Missing"}
        />
        <SummaryRow
          label="Certifications"
          value={
            profile.certifications.length === 0
              ? "None yet"
              : `${profile.certifications.length} document${profile.certifications.length === 1 ? "" : "s"}`
          }
          last
        />
      </div>

      {complete.isError && (
        <p className="text-sm text-destructive">
          {(complete.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Could not submit. Please review your details and try again."}
        </p>
      )}

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={complete.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          <CheckCircle2 className="h-4 w-4" />
          {complete.isPending ? "Submitting…" : "Submit for review"}
        </button>
      </div>
    </form>
  );
}

function SummaryRow({
  label,
  value,
  last = false,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={
        "flex items-center justify-between gap-3 px-4 py-3 text-sm" +
        (last ? "" : " border-b border-border")
      }
    >
      <span className="text-copy-muted">{label}</span>
      <span className="text-right font-medium text-copy">{value}</span>
    </div>
  );
}
